// import { IVitalDataSetting, TVitalSettingsResponse } from '../../../../types/vital-data';

import { IVitalDataSetting, TVitalSettingsResponse } from '../../types/life-id';

/**
 * Example of data shape
 */
export const temperatureSetting: IVitalDataSetting = {
  range: {
    min: 33,
    max: 44,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 38.1,
      max: 44,
    },
    {
      state: 'raised',
      min: 37,
      max: 38,
    },
    {
      state: 'normal',
      min: 36,
      max: 36.9,
    },
    {
      state: 'lowered',
      min: 35,
      max: 35.9,
    },
    {
      state: 'low',
      min: 33,
      max: 34.9,
    },
  ],
  labeledStates: [
    {
      state: 'hypothermia',
      min: 33,
      max: 34.9,
    },
    {
      state: 'subnormal',
      min: 35,
      max: 35.9,
    },
    {
      state: 'normalTemperature',
      min: 36,
      max: 36.9,
    },
    {
      state: 'subfebrilita',
      min: 37,
      max: 38,
    },
    {
      state: 'febrility',
      min: 38.1,
      max: 38.8,
    },
    {
      state: 'pyrexia',
      min: 38.9,
      max: 39.9,
    },
    {
      state: 'hyperpyrexia',
      min: 40,
      max: 44,
    },
  ],
};

export const hearthRateSettings: IVitalDataSetting = {
  range: {
    min: 25,
    max: 220,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 99,
      max: 220,
    },
    {
      state: 'normal',
      min: 60,
      max: 99,
    },
    {
      state: 'lowered',
      min: 50,
      max: 59.9,
    },
    {
      state: 'low',
      min: 25,
      max: 49.9,
    },
  ],
  labeledStates: [
    {
      state: 'tachycardia',
      min: 100,
      max: 220,
    },
    {
      state: 'normalHearthRate',
      min: 60,
      max: 99,
    },
    {
      state: 'bradycardia',
      min: 25,
      max: 59.9,
    },
  ],
};

export const diastolicSettings: IVitalDataSetting = {
  range: {
    min: 20,
    max: 280,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 90,
      max: 280,
    },
    {
      state: 'normal',
      min: 80,
      max: 89.9,
    },
    {
      state: 'lowered',
      min: 60,
      max: 79.9,
    },
    {
      state: 'low',
      min: 20,
      max: 59.9,
    },
  ],
  labeledStates: [
    {
      state: 'hypertensionIV',
      min: 120,
      max: 280,
    },
    {
      state: 'hypertensionIII',
      min: 110,
      max: 119.9,
    },
    {
      state: 'hypertensionII',
      min: 100,
      max: 109.9,
    },
    {
      state: 'hypertensionI',
      min: 90,
      max: 99.9,
    },
    {
      state: 'prehypertension',
      min: 85,
      max: 89.9,
    },
    {
      state: 'normalDiastolic',
      min: 80,
      max: 84.9,
    },
    {
      state: 'prehypotension',
      min: 60,
      max: 79.9,
    },
    {
      state: 'hypotension',
      min: 20,
      max: 59.9,
    },
  ],
};

export const systolicSettings: IVitalDataSetting = {
  range: {
    min: 20,
    max: 280,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 140,
      max: 280,
    },
    {
      state: 'normal',
      min: 120,
      max: 139.9,
    },
    {
      state: 'lowered',
      min: 90,
      max: 119.9,
    },
    {
      state: 'low',
      min: 20,
      max: 89.9,
    },
  ],
  labeledStates: [
    {
      state: 'hypertensionIV',
      min: 210,
      max: 280,
    },
    {
      state: 'hypertensionIII',
      min: 180,
      max: 209.9,
    },
    {
      state: 'hypertensionII',
      min: 160,
      max: 179.9,
    },
    {
      state: 'hypertensionI',
      min: 140,
      max: 159.9,
    },
    {
      state: 'prehypertension',
      min: 130,
      max: 139.9,
    },
    {
      state: 'normalSystolic',
      min: 120,
      max: 129.9,
    },
    {
      state: 'prehypotension',
      min: 90,
      max: 119.9,
    },
    {
      state: 'hypotension',
      min: 20,
      max: 89.9,
    },
  ],
};

export const cholesterolSettings: IVitalDataSetting = {
  range: {
    min: 1,
    max: 17.5,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 6.3,
      max: 17.5,
    },
    {
      state: 'raised',
      min: 5.2,
      max: 6.29,
    },
    {
      state: 'normal',
      min: 3,
      max: 5.19,
    },
    {
      state: 'low',
      min: 1,
      max: 2.99,
    },
  ],
  labeledStates: [
    {
      state: 'extremeCholesterol',
      min: 7.8,
      max: 17.5,
    },
    {
      state: 'highCholesterol',
      min: 6.3,
      max: 7.89,
    },
    {
      state: 'raisedCholesterol',
      min: 5.2,
      max: 6.29,
    },
    {
      state: 'normalCholesterol',
      min: 3,
      max: 5.19,
    },
    {
      state: 'lowCholesterol',
      min: 1,
      max: 2.99,
    },
  ],
};

export const glucoseSettings: IVitalDataSetting = {
  range: {
    min: 1,
    max: 38.9,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 38.9,
      max: 6.9,
    },
    {
      state: 'raised',
      min: 5.6,
      max: 6.9,
    },
    {
      state: 'normal',
      min: 3.9,
      max: 5.5,
    },
    {
      state: 'low',
      min: 1,
      max: 3.89,
    },
  ],
  labeledStates: [
    {
      state: 'hyperglycemia',
      min: 5.6,
      max: 38.9,
    },
    {
      state: 'normalGlucose',
      min: 3.9,
      max: 5.59,
    },
    {
      state: 'hypoglycemia',
      min: 3.89,
      max: 1,
    },
  ],
};

export const bodyMeasureSettings: IVitalDataSetting = {
  range: {
    min: 0,
    max: 50,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 24.9,
      max: 50,
    },
    {
      state: 'normal',
      min: 18.5,
      max: 24.9,
    },
    {
      state: 'low',
      min: 0,
      max: 18.49,
    },
  ],
  labeledStates: [
    {
      state: 'obeseIII',
      min: 40,
      max: 50,
    },
    {
      state: 'obeseII',
      min: 35,
      max: 39.9,
    },
    {
      state: 'obeseI',
      min: 30,
      max: 34.9,
    },
    {
      state: 'overweight',
      min: 25,
      max: 29.9,
    },
    {
      state: 'normalBmi',
      min: 18.5,
      max: 24.9,
    },
    {
      state: 'malnutrition',
      min: 0,
      max: 18.49,
    },
  ],
};

export const breathSettings: IVitalDataSetting = {
  range: {
    min: 8,
    max: 40,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 20,
      max: 40,
    },
    {
      state: 'normal',
      min: 12,
      max: 19.9,
    },
    {
      state: 'low',
      min: 8,
      max: 11.9,
    },
  ],
  labeledStates: [
    {
      state: 'tachypnoe',
      min: 20,
      max: 40,
    },
    {
      state: 'normalBreathing',
      min: 12,
      max: 19.9,
    },
    {
      state: 'bradypnoe',
      min: 8,
      max: 11.9,
    },
  ],
};

export const oxygenSettings: IVitalDataSetting = {
  range: {
    min: 80,
    max: 100,
  },
  graphIndicatorStates: [
    {
      state: 'normal',
      min: 95,
      max: 100,
    },
    {
      state: 'lowered',
      min: 93,
      max: 94.9,
    },
    {
      state: 'low',
      min: 80,
      max: 92.9,
    },
  ],
  labeledStates: [
    {
      state: 'normalOxygen',
      min: 95,
      max: 100,
    },
    {
      state: 'loweredOxygen',
      min: 90,
      max: 94.9,
    },
    {
      state: 'lowOxygen',
      max: 89.99,
      min: 80,
    },
  ],
};

export const bodyWeightSettings: IVitalDataSetting = {
  range: {
    min: 0,
    max: 500,
  },
  graphIndicatorStates: [],
  labeledStates: [],
};

export const bodyHeightSettings: IVitalDataSetting = {
  range: {
    min: 10,
    max: 350,
  },
  graphIndicatorStates: [],
  labeledStates: [],
};

export const waistSettings: IVitalDataSetting = {
  range: {
    min: 0,
    max: 300,
  },
  graphIndicatorStates: [
    {
      state: 'high',
      min: 102.1,
      max: 300,
    },
    {
      state: 'normal',
      min: 0,
      max: 94,
    },
    {
      state: 'raised',
      min: 94.1,
      max: 102,
    },
  ],
  labeledStates: [
    {
      state: 'high',
      min: 102.1,
      max: 300,
    },
    {
      state: 'raised',
      min: 94,
      max: 101.99,
    },
    {
      state: 'normal',
      min: 0,
      max: 94,
    },
  ],
};

/**
 * Partial example response
 */
export const vitalDataDefaultSettings: TVitalSettingsResponse = {
  temperature: temperatureSetting,
  heartRate: hearthRateSettings,
  bloodPressureDiastolic: diastolicSettings,
  bloodPressureSystolic: systolicSettings,
  cholesterol: cholesterolSettings,
  glucose: glucoseSettings,
  bodyMeasure: bodyMeasureSettings,
  breathRate: breathSettings,
  oxygenSaturation: oxygenSettings,
  bodyHeight: bodyHeightSettings,
  bodyWeight: bodyHeightSettings,
  waistCircumference: waistSettings,
};
